import { inject, provide, ref, shallowRef, type InjectionKey, type Ref } from 'vue'
import {
  getMarketPortalCategoryPage,
  getMarketPortalHome,
  getMarketPortalSearch,
  type MarketPortalCategoryVO,
  type MarketPortalFestivalTabVO,
  type MarketPortalHomeQuery,
  type MarketPortalSceneVO,
  type MarketPortalTradeRowVO
} from '@/api/market/portal'
import type { MarketTemplateVO } from '@/api/market/home'
import { getMarketTemplate } from '@/api/market/template'

export interface MarketPortalStore {
  loading: Ref<boolean>
  categories: Ref<MarketPortalCategoryVO[]>
  scenes: Ref<MarketPortalSceneVO[]>
  festivalTabs: Ref<MarketPortalFestivalTabVO[]>
  tradeRows: Ref<MarketPortalTradeRowVO[]>
  hotIds: Ref<number[]>
  hotTotal: Ref<number>
  hotOffset: Ref<number>
  sceneKey: Ref<number | undefined>
  cacheTemplate: (tpl: MarketTemplateVO) => void
  getTemplateName: (id: number) => string
  getTemplateBrief: (id: number) => string
  getCoverUrl: (id: number) => string | undefined
  getHotScore: (id: number) => number
  isPluginReady: (id: number) => boolean
  getNotReadyReason: (id: number) => string | undefined
  getPlayStyle: (id: number) => string | undefined
  ensureTemplate: (id: number) => Promise<void>
  loadHome: (params?: MarketPortalHomeQuery) => Promise<void>
  shuffleHot: () => Promise<void>
  loadCategoryPage: (params: {
    categoryId: number
    sortMode?: number
    pageNo?: number
    pageSize?: number
    sceneKey?: number
    festivalKey?: number
  }) => Promise<{ ids: number[]; total: number }>
  searchTemplates: (keyword: string, pageSize?: number) => Promise<number[]>
}

const MARKET_PORTAL_STORE_KEY: InjectionKey<MarketPortalStore> = Symbol('MarketPortalStore')

function toNum(v: unknown): number {
  return typeof v === 'number' ? v : Number(v || 0)
}

export function provideMarketPortalStore() {
  const loading = ref(false)
  const categories = ref<MarketPortalCategoryVO[]>([])
  const scenes = ref<MarketPortalSceneVO[]>([])
  const festivalTabs = ref<MarketPortalFestivalTabVO[]>([])
  const tradeRows = ref<MarketPortalTradeRowVO[]>([])
  const hotIds = ref<number[]>([])
  const hotTotal = ref(0)
  const hotOffset = ref(0)
  const sceneKey = ref<number>()
  const templateMap = shallowRef(new Map<number, MarketTemplateVO>())

  const cacheTemplate = (tpl: MarketTemplateVO) => {
    if (!tpl?.id) return
    const next = new Map(templateMap.value)
    next.set(tpl.id, { ...next.get(tpl.id), ...tpl })
    templateMap.value = next
  }

  const cacheTemplates = (list?: MarketTemplateVO[]) => {
    list?.forEach(cacheTemplate)
  }

  const getTemplateName = (id: number) => templateMap.value.get(id)?.name || `模板 #${id}`
  const getTemplateBrief = (id: number) => templateMap.value.get(id)?.brief || '暂无简介'
  const getCoverUrl = (id: number) => templateMap.value.get(id)?.coverUrl
  const getHotScore = (id: number) => toNum(templateMap.value.get(id)?.hotScore)
  const isPluginReady = (id: number) => templateMap.value.get(id)?.pluginReady === true
  const getNotReadyReason = (id: number) => templateMap.value.get(id)?.notReadyReason
  const getPlayStyle = (id: number) => templateMap.value.get(id)?.playStyle

  const ensureTemplate = async (id: number) => {
    if (templateMap.value.has(id)) return
    const tpl = await getMarketTemplate(id)
    cacheTemplate(tpl)
  }

  const loadHome = async (params?: MarketPortalHomeQuery) => {
    if (params?.sceneKey !== undefined) {
      sceneKey.value = params.sceneKey
      hotOffset.value = params.hotOffset ?? 0
    } else if (params?.hotOffset != null) {
      hotOffset.value = params.hotOffset
    }
    loading.value = true
    try {
      const data = await getMarketPortalHome({
        sceneKey: sceneKey.value,
        hotOffset: hotOffset.value
      })
      categories.value = data.categories || []
      scenes.value = data.scenes || []
      festivalTabs.value = data.festivalTabs || []
      tradeRows.value = data.tradeRows || []
      hotTotal.value = data.hotTotal || 0
      cacheTemplates(data.hotTemplates)
      festivalTabs.value.forEach((tab) => cacheTemplates(tab.templates))
      tradeRows.value.forEach((row) => cacheTemplates(row.templates))
      hotIds.value = (data.hotTemplates || []).map((t) => t.id!).filter(Boolean)
    } finally {
      loading.value = false
    }
  }

  const shuffleHot = async () => {
    const total = Math.max(1, hotTotal.value)
    hotOffset.value = (hotOffset.value + 12) % total
    await loadHome({ hotOffset: hotOffset.value, sceneKey: sceneKey.value })
  }

  const loadCategoryPage = async (params: {
    categoryId: number
    sortMode?: number
    pageNo?: number
    pageSize?: number
    sceneKey?: number
    festivalKey?: number
  }) => {
    loading.value = true
    try {
      const data = await getMarketPortalCategoryPage({
        categoryId: params.categoryId,
        sortMode: params.sortMode ?? 0,
        pageNo: params.pageNo ?? 1,
        pageSize: params.pageSize ?? 10,
        sceneKey: params.sceneKey,
        festivalKey: params.festivalKey
      })
      cacheTemplates(data.list)
      return {
        ids: (data.list || []).map((t) => t.id!).filter(Boolean),
        total: data.total || 0
      }
    } finally {
      loading.value = false
    }
  }

  const searchTemplates = async (keyword: string, pageSize = 20) => {
    loading.value = true
    try {
      const data = await getMarketPortalSearch({ keyword, pageNo: 1, pageSize })
      cacheTemplates(data.list)
      return (data.list || []).map((t) => t.id!).filter(Boolean)
    } finally {
      loading.value = false
    }
  }

  const store: MarketPortalStore = {
    loading,
    categories,
    scenes,
    festivalTabs,
    tradeRows,
    hotIds,
    hotTotal,
    hotOffset,
    sceneKey,
    cacheTemplate,
    getTemplateName,
    getTemplateBrief,
    getCoverUrl,
    getHotScore,
    isPluginReady,
    getNotReadyReason,
    getPlayStyle,
    ensureTemplate,
    loadHome,
    shuffleHot,
    loadCategoryPage,
    searchTemplates
  }

  provide(MARKET_PORTAL_STORE_KEY, store)
  return store
}

export function useMarketPortalStore() {
  const store = inject(MARKET_PORTAL_STORE_KEY)
  if (!store) throw new Error('useMarketPortalStore must be used within MarketPortal')
  return store
}
